import { MdHub } from 'react-icons/md';
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BsRobot,
  BsCodeSlash,
  BsArrowRight,
  BsChevronLeft,
  BsChevronRight,
  BsGrid3X3Gap,
  BsSearch,
  BsThreeDots,
} from 'react-icons/bs';
import HubItemCard from './HubItemCard';
import { loadAgentsApi, loadProgramsApi } from '../../../api';

const ITEMS_PER_PAGE = 9;
const ITEMS_PER_PAGE_COMPACT = 6;

// Enhanced Pagination Component with better UX
const PaginationControls = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemName,
}) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = window.innerWidth < 768 ? 3 : 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (window.innerWidth < 768) {
        // Mobile: show current, prev, next only
        if (currentPage === 1) {
          pages.push(1, 2, '...', totalPages);
        } else if (currentPage === totalPages) {
          pages.push(1, '...', totalPages - 1, totalPages);
        } else {
          pages.push(1, '...', currentPage, '...', totalPages);
        }
      } else {
        // Desktop: original logic
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push('...');
          for (let i = totalPages - 3; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          pages.push(currentPage - 1);
          pages.push(currentPage);
          pages.push(currentPage + 1);
          pages.push('...');
          pages.push(totalPages);
        }
      }
    }

    return pages;
  };

  return (
    <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-2 gap-md-3">
      <div className="d-flex align-items-center gap-2">
        {/* Previous Button */}
        <button
          className="btn btn-outline-success rounded-circle p-0 d-flex align-items-center justify-content-center"
          style={{ width: '36px', height: '36px', minWidth: '36px' }}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <BsChevronLeft size={16} />
        </button>

        {/* Page Numbers */}
        <div className="d-flex align-items-center gap-1">
          {getPageNumbers().map((page, index) => (
            <div key={index}>
              {page === '...' ? (
                <span className="px-1 text-muted d-none d-sm-inline">
                  <BsThreeDots size={20} />
                </span>
              ) : (
                <button
                  className={`btn ${
                    currentPage === page
                      ? 'btn-success'
                      : 'btn-outline-secondary'
                  } rounded-circle p-0 d-flex align-items-center justify-content-center`}
                  style={{
                    width: '36px',
                    height: '36px',
                    minWidth: '36px',
                    fontSize: '0.875rem',
                    border:
                      currentPage === page
                        ? 'none'
                        : '1px solid var(--border-color)',
                  }}
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Next Button */}
        <button
          className="btn btn-outline-success rounded-circle p-0 d-flex align-items-center justify-content-center"
          style={{ width: '36px', height: '36px', minWidth: '36px' }}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <BsChevronRight size={16} />
        </button>
      </div>

      {/* Page Info */}
      <div className="text-muted small text-center">
        Page {currentPage} of {totalPages}
      </div>
    </div>
  );
};

const HubSearchResults = ({
  initialAgents = [],
  initialPrograms = [],
  showStats = true,
}) => {
  const [agents, setAgents] = useState(initialAgents);
  const [programs, setPrograms] = useState(initialPrograms);
  const [searchType, setSearchType] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination states
  const [agentsPage, setAgentsPage] = useState(1);
  const [programsPage, setProgramsPage] = useState(1);
  const [totalAgents, setTotalAgents] = useState(0);
  const [totalPrograms, setTotalPrograms] = useState(0);

  // Track if we've done initial load
  const hasInitialized = useRef(false);
  // Store the search timeout
  const searchTimeout = useRef(null);

  const loadData = useCallback(async (query, type, agentPage, programPage) => {
    setIsLoading(true);

    try {
      const promises = [];

      // Calculate itemsPerPage based on the type parameter
      const itemsPerPage =
        type === 'all' ? ITEMS_PER_PAGE_COMPACT : ITEMS_PER_PAGE;

      if (type === 'all' || type === 'agents') {
        const agentOffset = (agentPage - 1) * itemsPerPage;
        promises.push(
          loadAgentsApi(query, agentOffset, itemsPerPage).then((data) => {
            setAgents(data.results || []);
            setTotalAgents(data.count || 0);
          })
        );
      }

      if (type === 'all' || type === 'programs') {
        const programOffset = (programPage - 1) * itemsPerPage;
        promises.push(
          loadProgramsApi(query, programOffset, itemsPerPage).then((data) => {
            setPrograms(data.results || []);
            setTotalPrograms(data.count || 0);
          })
        );
      }

      await Promise.all(promises);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load only
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      loadData('', 'all', 1, 1);
    }
  }, [loadData]);

  // Handle search with debouncing
  const handleSearch = (query) => {
    setSearchQuery(query);

    // Clear existing timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set new timeout for debounced search
    searchTimeout.current = setTimeout(() => {
      setAgentsPage(1);
      setProgramsPage(1);
      loadData(query, searchType, 1, 1);
    }, 300);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const handleTypeChange = (type) => {
    setSearchType(type);
    setAgentsPage(1);
    setProgramsPage(1);
    loadData(searchQuery, type, 1, 1);
  };

  const handleAgentsPageChange = (newPage) => {
    setAgentsPage(newPage);
    loadData(searchQuery, searchType, newPage, programsPage);
  };

  const handleProgramsPageChange = (newPage) => {
    setProgramsPage(newPage);
    loadData(searchQuery, searchType, agentsPage, newPage);
  };

  const getPlaceholder = () => {
    switch (searchType) {
      case 'agents':
        return 'Search AI agents...';
      case 'programs':
        return 'Search programs...';
      default:
        return 'Search agents and programs...';
    }
  };

  const shouldShowAgents = searchType === 'all' || searchType === 'agents';
  const shouldShowPrograms = searchType === 'all' || searchType === 'programs';

  // Calculate itemsPerPage for pagination display
  const itemsPerPage =
    searchType === 'all' ? ITEMS_PER_PAGE_COMPACT : ITEMS_PER_PAGE;

  const totalAgentsPages = Math.ceil(totalAgents / itemsPerPage);
  const totalProgramsPages = Math.ceil(totalPrograms / itemsPerPage);

  return (
    <div className="hub-search-results mb-5 pb-5">
      <div className="d-flex align-items-center justify-content-center mb-5">
        <motion.div
          animate={{ rotate: [0, 15, -15, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          className="text-success me-2"
        >
          <MdHub size={24} />
        </motion.div>
        <h4 className="mb-0 fw-bold">Hub</h4>
        <motion.div
          animate={{ rotate: [0, -15, 15, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          className="text-success ms-2"
        >
          <MdHub size={24} />
        </motion.div>
      </div>

      {/* Search Section */}
      <div className="search-section mb-5">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Toggle Buttons */}
              <div className="d-flex justify-content-center mb-3">
                <div
                  className="border rounded-pill p-1 shadow-sm"
                  style={{
                    backgroundColor: 'var(--bg-surface)',
                    borderColor: 'var(--border-color)',
                  }}
                >
                  <div className="d-flex gap-1">
                    {[
                      {
                        value: 'all',
                        label: 'All',
                        icon: <BsGrid3X3Gap size={16} />,
                      },
                      {
                        value: 'agents',
                        label: 'Agents',
                        icon: <BsRobot size={16} />,
                      },
                      {
                        value: 'programs',
                        label: 'Programs',
                        icon: <BsCodeSlash size={16} />,
                      },
                    ].map((option) => (
                      <button
                        key={option.value}
                        className={`btn ${
                          searchType === option.value ? 'btn-success' : ''
                        } rounded-pill px-4 py-2 d-flex align-items-center gap-2`}
                        onClick={() => handleTypeChange(option.value)}
                        style={{
                          minWidth: '100px',
                          transition: 'all 0.3s ease',
                          ...(searchType !== option.value && {
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: 'var(--text-secondary)',
                          }),
                        }}
                      >
                        {option.icon}
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Search Input */}
              <div className="position-relative">
                <BsSearch
                  size={20}
                  className="position-absolute text-muted"
                  style={{
                    left: '1.25rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                />
                <input
                  type="text"
                  className="form-control form-control-lg shadow"
                  style={{
                    borderRadius: '16px',
                    paddingLeft: '3.5rem',
                    height: '60px',
                    backgroundColor: 'var(--bg-surface)',
                    border: '1px solid var(--border-color)',
                  }}
                  placeholder={getPlaceholder()}
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Results Container */}
      <div style={{ minHeight: '800px' }}>
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-5"
            >
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Searching...</p>
            </motion.div>
          ) : (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Agents Section */}
              {shouldShowAgents && (
                <div className="mb-5">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center gap-2">
                      <BsRobot size={32} className="text-success" />
                      <h4 className="mb-0 fw-bold">
                        {searchQuery
                          ? 'Search Results - Agents'
                          : 'Latest Agents'}
                      </h4>
                      {totalAgents > 0 && (
                        <>
                          <span className="badge bg-success bg-opacity-10 text-success">
                            {totalAgents.toLocaleString()} total
                          </span>
                          <span className="text-muted small">
                            Showing {(agentsPage - 1) * itemsPerPage + 1}-
                            {Math.min(agentsPage * itemsPerPage, totalAgents)}
                          </span>
                        </>
                      )}
                    </div>
                    {searchType !== 'programs' && (
                      <a
                        href="https://app.quartermaster.io/hub/agents"
                        className="btn btn-link btn-sm text-decoration-none text-success"
                      >
                        Browse All <BsArrowRight />
                      </a>
                    )}
                  </div>

                  <div className="row g-4 mb-4">
                    {agents.length > 0 ? (
                      agents.map((agent, index) => (
                        <div key={agent.id} className="col-lg-4 col-md-6">
                          <HubItemCard
                            item={agent}
                            type="agent"
                            index={index}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="col-12">
                        <div className="alert alert-light text-center py-4">
                          <BsRobot
                            size={48}
                            className="mb-3 text-muted opacity-50"
                          />
                          <h6>No agents found</h6>
                          <p className="mb-0 text-muted small">
                            {searchQuery
                              ? 'Try different keywords'
                              : 'Start searching to find agents'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Agents Pagination */}
                  {totalAgentsPages > 1 && (
                    <PaginationControls
                      currentPage={agentsPage}
                      totalPages={totalAgentsPages}
                      onPageChange={handleAgentsPageChange}
                      totalItems={totalAgents}
                      itemName="agents"
                    />
                  )}
                </div>
              )}

              {/* Programs Section */}
              {shouldShowPrograms && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center gap-2">
                      <BsCodeSlash size={32} className="text-success" />
                      <h4 className="mb-0 fw-bold">
                        {searchQuery
                          ? 'Search Results - Programs'
                          : 'Latest Programs'}
                      </h4>
                      {totalPrograms > 0 && (
                        <>
                          <span className="badge bg-success bg-opacity-10 text-success">
                            {totalPrograms.toLocaleString()} total
                          </span>
                          <span className="text-muted small">
                            Showing {(programsPage - 1) * itemsPerPage + 1}-
                            {Math.min(
                              programsPage * itemsPerPage,
                              totalPrograms
                            )}
                          </span>
                        </>
                      )}
                    </div>
                    {searchType !== 'agents' && (
                      <a
                        href="https://app.quartermaster.io/hub/programs"
                        className="btn btn-link btn-sm text-decoration-none text-success"
                      >
                        Browse All <BsArrowRight />
                      </a>
                    )}
                  </div>

                  <div className="row g-4 mb-4">
                    {programs.length > 0 ? (
                      programs.map((program, index) => (
                        <div key={program.id} className="col-lg-4 col-md-6">
                          <HubItemCard
                            item={program}
                            type="program"
                            index={index}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="col-12">
                        <div className="alert alert-light text-center py-4">
                          <BsCodeSlash
                            size={48}
                            className="mb-3 text-muted opacity-50"
                          />
                          <h6>No programs found</h6>
                          <p className="mb-0 text-muted small">
                            {searchQuery
                              ? 'Try different keywords'
                              : 'Start searching to find programs'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Programs Pagination */}
                  {totalProgramsPages > 1 && (
                    <PaginationControls
                      currentPage={programsPage}
                      totalPages={totalProgramsPages}
                      onPageChange={handleProgramsPageChange}
                      totalItems={totalPrograms}
                      itemName="programs"
                    />
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default HubSearchResults;
